import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
    ArrowLeft,
    Mail,
    Phone,
    MapPin,
    Edit,
    Trash2,
    Plus,
    FileText,
    Clock,
    Copy,
    ExternalLink,
    Building
} from 'lucide-react'
import { useApp } from '../context/AppContext'
import ClientModal from '../components/ClientModal'

export default function ClientView() {
    const { id } = useParams()
    const navigate = useNavigate()
    const { getClientById, updateClient, deleteClient, invoices, addNotification } = useApp()
    const [client, setClient] = useState(null)
    const [clientInvoices, setClientInvoices] = useState([])
    const [showModal, setShowModal] = useState(false)

    useEffect(() => {
        const c = getClientById(id)
        if (c) {
            setClient(c)
            // Filter invoices for this client
            const filtered = invoices.filter(inv => inv.clientId === id).sort((a, b) => {
                return new Date(b.createdAt?.toDate?.() || b.createdAt) - new Date(a.createdAt?.toDate?.() || a.createdAt)
            })
            setClientInvoices(filtered)
        } else {
            // Client not found, redirect to list
            navigate('/clients')
        }
    }, [id, getClientById, invoices, navigate])

    if (!client) return null

    const stats = {
        totalBilled: clientInvoices.reduce((sum, inv) => sum + inv.total, 0),
        paidAmount: clientInvoices
            .filter(inv => inv.status === 'paid')
            .reduce((sum, inv) => sum + inv.total, 0),
        outstanding: clientInvoices
            .filter(inv => ['sent', 'viewed', 'partial', 'overdue'].includes(inv.status))
            .reduce((sum, inv) => sum + (inv.total - (inv.partialAmount || 0)), 0),
    }

    const handleDelete = async () => {
        if (clientInvoices.length > 0) {
            addNotification(`Cannot delete client with ${clientInvoices.length} associated invoices`, 'error')
            return
        }

        if (window.confirm(`Are you sure you want to delete ${client.name}?`)) {
            await deleteClient(id)
            addNotification('Client deleted successfully', 'success')
            navigate('/clients')
        }
    }

    const handleCopyPortalLink = () => {
        const link = `${window.location.origin}/portal/client/${client.id}`
        navigator.clipboard.writeText(link)
        addNotification('Portal link copied to clipboard', 'success')
    }

    const getStatusColor = (status) => {
        switch (status) {
            case 'paid': return 'bg-green-100 text-green-700'
            case 'overdue': return 'bg-red-100 text-red-700'
            case 'draft': return 'bg-gray-100 text-gray-700'
            case 'sent': return 'bg-blue-100 text-blue-700'
            default: return 'bg-yellow-100 text-yellow-700'
        }
    }

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/clients')}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors btn-touch"
                    >
                        <ArrowLeft size={24} className="text-gray-600" />
                    </button>
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate max-w-[200px] sm:max-w-none">
                            {client.name}
                        </h1>
                        <p className="text-gray-500 text-sm">Client Details</p>
                    </div>
                </div>

                <div className="flex flex-wrap gap-2 sm:ml-auto">
                    <button
                        onClick={handleCopyPortalLink}
                        className="btn btn-secondary flex-1 sm:flex-none py-2 text-sm sm:text-base"
                    >
                        <Copy size={16} className="sm:w-[18px] sm:h-[18px]" />
                        <span className="whitespace-nowrap">Portal Link</span>
                    </button>
                    <button
                        onClick={() => setShowModal(true)}
                        className="btn btn-primary flex-1 sm:flex-none py-2 text-sm sm:text-base"
                    >
                        <Edit size={16} className="sm:w-[18px] sm:h-[18px]" />
                        <span>Edit</span>
                    </button>
                    <button
                        onClick={handleDelete}
                        className="btn btn-danger py-2 px-3 sm:px-4"
                        title="Delete Client"
                    >
                        <Trash2 size={16} className="sm:w-[18px] sm:h-[18px]" />
                        <span className="sm:inline hidden">Delete</span>
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Client Info Card */}
                <div className="card p-6 h-fit">
                    <h3 className="font-semibold text-gray-900 mb-4">Contact Information</h3>
                    <div className="space-y-4">
                        {client.businessName && (
                            <div className="flex items-center gap-3 text-gray-600">
                                <Building size={18} className="text-gray-400" />
                                <span className="font-medium">{client.businessName}</span>
                            </div>
                        )}
                        <div className="flex items-center gap-3 text-gray-600">
                            <Mail size={18} className="text-gray-400" />
                            <a href={`mailto:${client.email}`} className="hover:text-primary-600">
                                {client.email}
                            </a>
                        </div>
                        {client.phone && (
                            <div className="flex items-center gap-3 text-gray-600">
                                <Phone size={18} className="text-gray-400" />
                                <a href={`tel:${client.phone}`} className="hover:text-primary-600">
                                    {client.phone}
                                </a>
                            </div>
                        )}
                        {client.address && (
                            <div className="flex items-start gap-3 text-gray-600">
                                <MapPin size={18} className="text-gray-400 mt-1" />
                                <span className="whitespace-pre-line">{client.address}</span>
                            </div>
                        )}
                        <div className="pt-4 mt-4 border-t border-gray-100">
                            <p className="text-sm text-gray-500">
                                Currency: <span className="font-medium text-gray-900">{client.currency || 'USD'}</span>
                            </p>
                            <p className="text-sm text-gray-500 mt-1">
                                Added: {client.createdAt ? new Date(client.createdAt?.toDate?.() || client.createdAt).toLocaleDateString() : 'N/A'}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Stats & History */}
                <div className="md:col-span-2 space-y-6">
                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                        <div className="card p-3 sm:p-4">
                            <p className="text-[10px] sm:text-sm text-gray-500 uppercase tracking-wider mb-1">Total Billed</p>
                            <p className="text-lg sm:text-2xl font-bold text-gray-900">
                                ${stats.totalBilled.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                            </p>
                        </div>
                        <div className="card p-3 sm:p-4">
                            <p className="text-[10px] sm:text-sm text-gray-500 uppercase tracking-wider mb-1">Paid</p>
                            <p className="text-lg sm:text-2xl font-bold text-green-600">
                                ${stats.paidAmount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                            </p>
                        </div>
                        <div className="card p-3 sm:p-4 col-span-2 sm:col-span-1">
                            <p className="text-[10px] sm:text-sm text-gray-500 uppercase tracking-wider mb-1">Outstanding</p>
                            <p className={`text-lg sm:text-2xl font-bold ${stats.outstanding > 0 ? 'text-orange-600' : 'text-gray-900'}`}>
                                ${stats.outstanding.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                            </p>
                        </div>
                    </div>

                    {/* Invoice History */}
                    <div className="card">
                        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                            <h3 className="font-semibold text-gray-900">Invoice History</h3>
                            <Link to="/invoices/new" className="text-sm text-primary-600 font-medium hover:text-primary-700">
                                + New Invoice
                            </Link>
                        </div>

                        {clientInvoices.length === 0 ? (
                            <div className="p-8 text-center text-gray-500">
                                <FileText size={48} className="mx-auto text-gray-200 mb-3" />
                                <p>No invoices created for this client yet.</p>
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-100">
                                {clientInvoices.map(invoice => (
                                    <Link
                                        to={`/invoices/${invoice.id}/view`}
                                        key={invoice.id}
                                        className="block p-4 hover:bg-gray-50 transition-colors"
                                    >
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="flex items-center gap-3 sm:gap-4 overflow-hidden">
                                                <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${getStatusColor(invoice.status).replace('text-', 'bg-opacity-20 ')}`}>
                                                    <FileText size={16} />
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="font-medium text-gray-900 truncate">{invoice.invoiceNumber}</p>
                                                    {invoice.subject && (
                                                        <p className="text-xs sm:text-sm font-semibold text-gray-800 truncate">{invoice.subject}</p>
                                                    )}
                                                    <p className="text-[10px] sm:text-xs text-gray-400 font-medium">
                                                        {new Date(invoice.createdAt?.toDate?.() || invoice.createdAt).toLocaleDateString()}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="text-right shrink-0">
                                                <p className="text-sm sm:text-base font-bold text-gray-900">${invoice.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                                                <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${getStatusColor(invoice.status)}`}>
                                                    {invoice.status}
                                                </span>
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Client Modal */}
            {showModal && (
                <ClientModal
                    client={client}
                    onClose={() => setShowModal(false)}
                    onSave={(clientData) => {
                        updateClient(client.id, clientData)
                        addNotification('Client updated successfully', 'success')
                        setShowModal(false)
                    }}
                />
            )}
        </div>
    )
}
